import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, of, catchError } from 'rxjs';
import { PaginateResult } from '../core/models/paginate-result.model';

export interface DashboardFilter {
  startDate?: string | Date;
  endDate?: string | Date;
  skip?: number;
  take?: number;
  orderBy?: string[];
  search?: string;
}

export interface QuoteWidgetSummaryModel {
  open: number;
  completed: number;
  potential: number;
  conversionRate: number;
}

@Injectable({ providedIn: 'root' })
export class DashboardMySalesService extends ApiService {
  constructor(http: HttpClient) {
    super(http, 'DashboardMySales');
  }

  private fetchPaginated(routeSuffix: string, filter: DashboardFilter): Observable<PaginateResult> {
    const params = this.buildFilterParams(filter);
    return this.http.get<PaginateResult>(`${this.route()}/${routeSuffix}`, { params }).pipe(this.handle403AsEmptyPaginate<PaginateResult>());
  }

  getQuotesSummary(filter: DashboardFilter): Observable<QuoteWidgetSummaryModel> {
    const params = this.buildFilterParams(filter);
    return this.http.get<QuoteWidgetSummaryModel>(this.route() + '/GetQuotesSummaryAsync', { params }).pipe(this.handle403AsZeroSummary<QuoteWidgetSummaryModel>());
  }

  getAllQuotesCreated(filter: DashboardFilter): Observable<PaginateResult> {
    return this.fetchPaginated('AllQuotesCreated', filter);
  }

  getAllQuotesCompleted(filter: DashboardFilter): Observable<PaginateResult> {
    return this.fetchPaginated('AllQuotesCompleted', filter);
  }

  getAllCustomersPaginated(filter: DashboardFilter): Observable<PaginateResult> {
    return this.fetchPaginated('AllCustomersPaginated', filter);
  }

  getQuotesCompletedWinRate(filter: DashboardFilter): Observable<PaginateResult> {
    return this.fetchPaginated('GetQuotesCompletedWinRate', filter);
  }

  private buildFilterParams(filter: DashboardFilter): HttpParams {
    let params = new HttpParams();

    if (filter.skip !== undefined) {
      params = params.set('Skip', String(filter.skip));
    }
    if (filter.take !== undefined) {
      params = params.set('Take', String(filter.take));
    }
    if (filter.search) {
      params = params.set('Search', filter.search);
    }

    if (filter.startDate) {
      params = params.set('StartDate', this.toIso(filter.startDate));
    }
    if (filter.endDate) {
      params = params.set('EndDate', this.toIso(filter.endDate));
    }

    if (filter.orderBy && filter.orderBy.length) {
      for (const ob of filter.orderBy) {
        params = params.append('orderBy', ob);
      }
    }

    return params;
  }

  private toIso(value: string | Date): string {
    if (value instanceof Date) return value.toISOString();
    const parsed = new Date(value);
    return !isNaN(parsed.getTime()) ? parsed.toISOString() : value;
  }

  private handle403AsEmptyPaginate<PaginateResult>(): (source: Observable<PaginateResult>) => Observable<PaginateResult> {
    return catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        return of({ total: 0, list: [] } as PaginateResult);
      }
      throw error;
    });
  }

  private handle403AsZeroSummary<T>(): (source: Observable<T>) => Observable<T> {
    return catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        const empty = { open: 0, completed: 0, potential: 0, conversionRate: 0 } as unknown as T;
        return of(empty);
      }
      throw error;
    });
  }
}
